import type { FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ODSAlert, ODSButton, ODSLoginPortal } from 'bc-ods';
import useAccountStore from 'stores/useAccountStore';
import type { AuthAccountWithPassType } from 'api-client/accounts';
import sameVersion from 'stores/compare/versions';
import { logoutUser } from 'api-client';
import { includeBasePath } from 'utils/url-utils';

const messages = defineMessages({
  pendingVerificationAlert: {
    id: 'pendingVerification.alert',
    defaultMessage: 'Your account has been successfully created and is currently pending approval.',
  },
  pendingVerificationInfo: {
    id: 'pendingVerification.info',
    defaultMessage: `You will receive an email notification once your account has been verified and activated. If you do not receive an email within 48 hours, please contact our support team for assistance.`,
  },
  accountRejectedAlert: {
    id: 'accountRejected.alert',
    defaultMessage: 'Your account creation has been rejected.',
  },
  accountRejectedInfo: {
    id: 'accountRejected.info',
    defaultMessage: `If you feel that this is an error please contact our support team for assistance.`,
  },
  returnToSignIn: {
    id: 'PendingVerification.returnToSignIn',
    defaultMessage: 'Return to sign in',
  },
});

const userVerificationNeeded = (user: AuthAccountWithPassType): boolean =>
  !!user.userVerificationEnabled && user.accountVerifiedStatus !== true;

const redirectToAppIfVerified = (user: AuthAccountWithPassType): boolean => {
  if (userVerificationNeeded(user)) return false;
  window.location.href = includeBasePath('/');
  return true;
};

const UserVerificationStatus: FC = () => {
  const handleLogout = () => {
    logoutUser();
  };
  const user = useAccountStore(
    ({ account }) => account ?? ({} as AuthAccountWithPassType),
    sameVersion
  );

  if (redirectToAppIfVerified(user)) return null;

  const status = user.accountVerifiedStatus === false ? 'accountRejected' : 'pendingVerification';

  return (
    <ODSLoginPortal data-testid="account-pending-form">
      <ODSAlert color={status === 'pendingVerification' ? 'success' : 'danger'} className="mb-3">
        <FormattedMessage {...messages[`${status}Alert`]} />
      </ODSAlert>
      <div className="fw-normal">
        <span className="d-inline-block">
          <FormattedMessage {...messages[`${status}Info`]} />
        </span>
      </div>
      <ODSButton onClick={handleLogout} className="mt-4" cta fullWidth data-testid="go-to-login">
        <FormattedMessage {...messages.returnToSignIn} />
      </ODSButton>
    </ODSLoginPortal>
  );
};

export default UserVerificationStatus;
